import { GLOBE_CITIES, formatCoords, type GlobeCity } from './cities';
import { COUNTRY_NAMES } from './countryNames';
import { GLOBE_FS, GLOBE_VS } from './shaders';

// Imperative port of `montaGlobo` from the IQ Elite prototype (public/previews/iq-elite).

const LAND_URL = '/elite/globe-land.png';
/** Figma: the sphere spans the 1244px-wide box with a 640px radius, top edge on the box top. */
const R_PER_BOX_WIDTH = 640 / 1244;
const BRAND: [number, number, number] = [81, 166, 103]; // #51A667
const BRAND_CSS = BRAND.join(',');
const DEG = 180 / Math.PI;
const BASE_TILT = -26;

export interface GlobeOptions {
  /** The layer (absolute inset-0 in the hero); offset parent of `box`. */
  root: HTMLElement;
  /** The globe box; the WebGL canvas is created inside it. Must not be transformed. */
  box: HTMLElement;
  arcs: HTMLCanvasElement;
  /** Pin nodes, each with [data-city], [data-coords] and [data-pulse] children. */
  pins: HTMLElement[];
  /** Country-name pill that follows the mouse ([data-name] child). */
  label: HTMLElement | null;
  reducedMotion: boolean;
  /** First frame with the land texture drawn. */
  onReady: () => void;
  /** WebGL context lost after start. */
  onLost: () => void;
}

interface Slot {
  el: HTMLElement;
  city: GlobeCity | null;
  id: number;
  s: number;
  target: number;
  t0: number;
}

type Vec3 = [number, number, number];
type ScreenPt = [number, number, number];

interface Flight {
  a: Vec3;
  b: Vec3;
  h: number;
  t0: number;
  dur: number;
  from: GlobeCity;
  to: GlobeCity;
  pinned: boolean;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const vec = (lon: number, lat: number): Vec3 => {
  const l = lon / DEG, b = lat / DEG;
  return [Math.cos(b) * Math.sin(l), Math.sin(b), Math.cos(b) * Math.cos(l)];
};
const fromVec = (v: Vec3): [number, number] => [Math.atan2(v[0], v[2]) * DEG, Math.asin(clamp(v[1], -1, 1)) * DEG];
const dot = (a: Vec3, b: Vec3) => clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1);
const slerp = (a: Vec3, b: Vec3, t: number): Vec3 => {
  const w = Math.acos(dot(a, b)), s = Math.sin(w) || 1;
  const ka = Math.sin((1 - t) * w) / s, kb = Math.sin(t * w) / s;
  return [a[0] * ka + b[0] * kb, a[1] * ka + b[1] * kb, a[2] * ka + b[2] * kb];
};
const easeInOutCubic = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

/** Returns a destroy function, or null when WebGL2 is unavailable (keep the fallback image). */
export function createGlobe(o: GlobeOptions): (() => void) | null {
  const { root, box, arcs, label, reducedMotion: reduced } = o;

  // A fresh canvas per mount: a lost context cannot be re-acquired from the same element
  // (React StrictMode mounts twice).
  const cv = document.createElement('canvas');
  cv.setAttribute('aria-hidden', 'true');
  cv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;touch-action:pan-y;';
  const gl = cv.getContext('webgl2', { premultipliedAlpha: true, antialias: false, alpha: true });
  const actx = arcs.getContext('2d');
  if (!gl || !actx) return null;
  box.appendChild(cv);

  // ── GL setup ──
  const compile = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.error(gl.getShaderInfoLog(s));
    return s;
  };
  const vs = compile(gl.VERTEX_SHADER, GLOBE_VS), fs = compile(gl.FRAGMENT_SHADER, GLOBE_FS);
  const prog = gl.createProgram()!;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.useProgram(prog);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aP = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(aP);
  gl.vertexAttribPointer(aP, 2, gl.FLOAT, false, 0, 0);
  const U = (n: string) => gl.getUniformLocation(prog, n);
  const u = {
    res: U('uRes'), c: U('uC'), r: U('uR'), rot: U('uRot'), tilt: U('uTilt'), dpr: U('uDpr'),
    mouse: U('uMouse'), hover: U('uHover'), spot: U('uSpot'), pais: U('uPais'), sel: U('uSel'),
    cor: U('uCor'), auto: U('uAuto'), autoS: U('uAutoS'), autoC: U('uAutoC'),
  };
  gl.uniform1i(U('uTerra'), 0);
  gl.uniform3f(u.cor, BRAND[0] / 255, BRAND[1] / 255, BRAND[2] / 255);
  const tex = gl.createTexture();
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  // ── land texture + a small CPU copy of the country ids (for the hover label) ──
  const IW = 1024, IH = 512;
  let ids: Uint8Array | null = null;
  let landReady = false;
  const abort = new AbortController();
  fetch(LAND_URL, { signal: abort.signal })
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
    .then((b) => createImageBitmap(b, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' }))
    .then((bmp) => {
      if (destroyed || gl.isContextLost()) { bmp.close(); return; }
      gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
      const c = document.createElement('canvas');
      c.width = IW; c.height = IH;
      const cx = c.getContext('2d', { willReadFrequently: true });
      if (cx) {
        cx.imageSmoothingEnabled = false;
        cx.drawImage(bmp, 0, 0, IW, IH);
        const px = cx.getImageData(0, 0, IW, IH).data;
        ids = new Uint8Array(IW * IH);
        for (let i = 0; i < IW * IH; i++) ids[i] = px[i * 4 + 1];
      }
      bmp.close();
      landReady = true;
      kick();
    })
    .catch(() => { /* fallback image stays */ });
  const idAt = (lon: number, lat: number) => {
    if (!ids) return 0;
    const x = ((Math.floor(((lon + 180) / 360) * IW) % IW) + IW) % IW;
    const y = clamp(Math.floor(((90 - lat) / 180) * IH), 0, IH - 1);
    return ids[y * IW + x];
  };

  // ── layout ──
  let W = 1, H = 1, dpr = 1, bL = 0, bT = 0, bW = 1, bH = 1;
  let geo = { cx: 0, cy: 0, R: 1 }, k = 1;
  const measure = () => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = root.clientWidth; H = root.clientHeight;
    bL = box.offsetLeft; bT = box.offsetTop; bW = box.offsetWidth || 1; bH = box.offsetHeight || 1;
    cv.width = Math.round(bW * dpr); cv.height = Math.round(bH * dpr);
    arcs.width = Math.round(W * dpr); arcs.height = Math.round(H * dpr);
    gl.viewport(0, 0, cv.width, cv.height);
    const R = bW * R_PER_BOX_WIDTH;
    geo = { cx: bL + bW / 2, cy: bT + R, R };
    k = R / 640;
    kick();
  };

  // ── state ──
  let rot = 30, tilt = BASE_TILT, targetTilt = BASE_TILT, tiltDrag = 0, dx = 0, targetDx = 0, vel = 0, giro = rot;
  let mx = -9999, my = -9999, hover = 0, targetHover = 0;
  let pais = 0, sel = 0, targetSel = 0;
  let drag: { id: number; x: number; y: number; x0: number; y0: number; axis: '' | 'x' | 'y' | 'xy' } | null = null;

  const onGlobe = (x: number, y: number) => Math.hypot(x - geo.cx, y - geo.cy) < geo.R;
  // screen → lon/lat (the shader's maths, inverted)
  const lonLat = (x: number, y: number): [number, number] | null => {
    const qx = (x - geo.cx) / geo.R, qy = -(y - geo.cy) / geo.R, r2 = qx * qx + qy * qy;
    if (r2 > 1) return null;
    const z = Math.sqrt(1 - r2), t = tilt / DEG;
    const y0 = qy * Math.cos(t) + z * Math.sin(t), z0 = -qy * Math.sin(t) + z * Math.cos(t);
    return [Math.atan2(qx, z0) * DEG - giro, Math.asin(clamp(y0, -1, 1)) * DEG];
  };
  // lon/lat (+ altitude) → screen; z > 0 means facing the camera
  const toScreen = (lon: number, lat: number, alt = 1): ScreenPt => {
    const l = (lon + giro) / DEG, b = lat / DEG, t = tilt / DEG;
    const x = Math.cos(b) * Math.sin(l) * alt, y0 = Math.sin(b) * alt, z0 = Math.cos(b) * Math.cos(l) * alt;
    const y = y0 * Math.cos(t) - z0 * Math.sin(t), z = y0 * Math.sin(t) + z0 * Math.cos(t);
    return [geo.cx + x * geo.R, geo.cy - y * geo.R, z];
  };

  // ── country label under the mouse ──
  const labelName = label?.querySelector<HTMLElement>('[data-name]') ?? null;
  const hoverCountry = () => {
    const ll = mx > -999 && targetHover ? lonLat(mx, my) : null;
    const id = ll ? idAt(ll[0], ll[1]) : 0;
    if (id && COUNTRY_NAMES[id]) {
      if (id !== pais) { pais = id; if (labelName) labelName.textContent = COUNTRY_NAMES[id]; }
      targetSel = 1;
      if (label) { label.dataset.on = 'on'; label.style.left = `${mx}px`; label.style.top = `${my}px`; }
    } else {
      targetSel = 0;
      if (label) label.dataset.on = '';
    }
  };

  // ── pins: cities cycle through up to 3 slots as they rotate into view ──
  const slots: Slot[] = o.pins.map((el) => ({ el, city: null, id: 0, s: 0, target: 0, t0: -99 }));
  let nextTour = 1.4;
  const pulses: Animation[] = [];
  if (!reduced) for (const el of o.pins) {
    const p = el.querySelector<HTMLElement>('[data-pulse]');
    if (p) pulses.push(p.animate(
      // grows from the pin (42/80) to the Figma ring size; reduced motion keeps the static Figma ring
      [{ transform: 'scale(0.525)', opacity: 1 }, { transform: 'scale(1)', opacity: 0 }],
      { duration: 2600, iterations: Infinity, easing: 'cubic-bezier(.2,.7,.3,1)' },
    ));
  }
  const inView = (c: GlobeCity, topo: number) => {
    const [x, y, z] = toScreen(c.lon, c.lat);
    return z > 0.12 && y > topo + 40 * k && y < H - 60 * k && x > W * 0.1 && x < W * 0.9;
  };
  const assign = (slot: Slot, c: GlobeCity, tt: number) => {
    slot.city = c; slot.id = COUNTRY_NAMES.indexOf(c.country); slot.t0 = tt; slot.target = 1;
    const city = slot.el.querySelector('[data-city]'), coords = slot.el.querySelector('[data-coords]');
    if (city) city.textContent = c.name;
    if (coords) coords.textContent = formatCoords(c.lat, c.lon);
    slot.el.dataset.side = toScreen(c.lon, c.lat)[0] > W * 0.6 ? 'left' : 'right';
    // restart the card fade
    slot.el.dataset.on = '';
    void slot.el.offsetWidth;
    slot.el.dataset.on = 'on';
  };
  const tour = (tt: number) => {
    if (!slots.length) return;
    const topo = bT;
    if (reduced) {
      // static: pin whatever cities are in view, no cycling
      for (const v of slots) if (v.city && !inView(v.city, topo)) { v.city = null; v.target = 0; }
      for (const c of GLOBE_CITIES) {
        if (slots.some((v) => v.city === c) || !inView(c, topo)) continue;
        const free = slots.find((v) => !v.city);
        if (!free) break;
        assign(free, c, tt);
      }
    } else if (tt > nextTour) {
      nextTour = tt + 1.2;
      const free = GLOBE_CITIES.filter((c) => !slots.some((v) => v.city === c && (v.target || tt - v.t0 < 12)) && inView(c, topo));
      const slot = slots.find((v) => !v.target && v.s < 0.05);
      if (free.length && slot) { assign(slot, free[0], tt); nextTour = tt + 2.2; }
    }
    const idsU = [0, 0, 0], sU = [0, 0, 0], cU = [0, 0, 0, 0, 0, 0];
    slots.forEach((v, i) => {
      if (!v.city) { v.el.style.opacity = '0'; v.el.dataset.on = ''; return; }
      const [x, y, z] = toScreen(v.city.lon, v.city.lat);
      if (!reduced && (z < 0.06 || y < topo + 20 * k || tt - v.t0 > 7)) v.target = 0;
      v.s = reduced ? v.target : v.s + (v.target - v.s) * 0.07;
      if (!v.target && v.s < 0.05) v.el.dataset.on = '';
      idsU[i] = Math.max(0, v.id); sU[i] = v.id > 0 ? v.s * 0.85 : 0; cU[i * 2] = v.city.lat; cU[i * 2 + 1] = v.city.lon;
      v.el.style.transform = `translate(${x.toFixed(1)}px,${y.toFixed(1)}px)`;
      v.el.style.opacity = Math.min(1, v.s * 1.4).toFixed(3);
    });
    gl.uniform3fv(u.auto, idsU); gl.uniform3fv(u.autoS, sU); gl.uniform2fv(u.autoC, cU);
  };

  // ── flights between the cities: an arc that lifts off the ground, green trail, lit tip,
  // a pulse on arrival; hidden behind the earth, never above the globe's top edge ──
  const flights: Flight[] = [];
  let nextFlight = 0.3, arcsTop = 0;
  const altitude = (a: Vec3, b: Vec3) => 0.02 + (0.07 * Math.acos(dot(a, b))) / Math.PI;
  const along = (a: Vec3, b: Vec3, t: number, h: number) => {
    const [lo, la] = fromVec(slerp(a, b, t));
    return toScreen(lo, la, 1 + h * Math.sin(Math.PI * t));
  };
  const visible = ([x, y, z]: ScreenPt) => y > arcsTop && (z > 0 || Math.hypot(x - geo.cx, y - geo.cy) > geo.R);
  const lit = (c: GlobeCity) => slots.some((q) => q.city === c && q.target);
  const drawArcs = (tt: number) => {
    actx.setTransform(dpr, 0, 0, dpr, 0, 0);
    actx.clearRect(0, 0, W, H);
    if (reduced) return;
    arcsTop = bT - 16 * k;
    if (tt > nextFlight && flights.length < 4) {
      const litCities = slots.filter((v) => v.city && v.target && v.s > 0.5).map((v) => v.city as GlobeCity);
      let a: GlobeCity, b: GlobeCity;
      if (litCities.length >= 2 && !flights.some((v) => v.pinned)) { a = litCities[0]; b = litCities[1]; }
      else {
        a = GLOBE_CITIES[Math.floor(Math.random() * GLOBE_CITIES.length)]; b = a;
        while (b === a) b = GLOBE_CITIES[Math.floor(Math.random() * GLOBE_CITIES.length)];
      }
      const va = vec(a.lon, a.lat), vb = vec(b.lon, b.lat), h = altitude(va, vb);
      flights.push({ a: va, b: vb, h, t0: tt, dur: 2.6 + h * 14, from: a, to: b, pinned: litCities.includes(a) && litCities.includes(b) });
      nextFlight = tt + 0.9 + Math.random() * 0.8;
    }
    const lw = Math.max(0.6, k);
    for (let i = flights.length - 1; i >= 0; i--) {
      const v = flights[i], age = tt - v.t0, pr = Math.min(1, age / v.dur);
      // a pin → pin flight stays drawn while both cities are lit (up to 5 s)
      const hold = v.pinned && lit(v.from) && lit(v.to) ? 5 : 0;
      const fade = Math.max(0, (age - v.dur - hold) / 0.9);
      if (fade >= 1) { flights.splice(i, 1); continue; }
      const head = easeInOutCubic(pr), N = 56, TRAIL = 0.42;
      let prev: ScreenPt | null = null;
      for (let s = 0; s <= N; s++) {
        const ts = (s / N) * head, p = along(v.a, v.b, ts, v.h);
        const q = pr >= 1 && v.pinned ? 1 : Math.max(0, 1 - (head - ts) / TRAIL);   // 0 at the trail end, 1 at the tip
        if (prev && q > 0 && visible(p) && visible(prev)) {
          actx.strokeStyle = `rgba(${BRAND_CSS},${(0.08 + 0.82 * q * q) * (1 - fade)})`;
          actx.lineWidth = (1 + q * 1.1) * lw;
          actx.beginPath(); actx.moveTo(prev[0], prev[1]); actx.lineTo(p[0], p[1]); actx.stroke();
        }
        prev = p;
      }
      if (pr < 1) {
        const p = along(v.a, v.b, head, v.h);
        if (visible(p)) {
          const g = actx.createRadialGradient(p[0], p[1], 0, p[0], p[1], 9 * lw);
          g.addColorStop(0, `rgba(${BRAND_CSS},.55)`); g.addColorStop(1, `rgba(${BRAND_CSS},0)`);
          actx.fillStyle = g; actx.beginPath(); actx.arc(p[0], p[1], 9 * lw, 0, 7); actx.fill();
          actx.fillStyle = '#fff'; actx.beginPath(); actx.arc(p[0], p[1], 2.4 * lw, 0, 7); actx.fill();   // 6px tip incl. stroke
          actx.strokeStyle = `rgb(${BRAND_CSS})`; actx.lineWidth = 1.2 * lw; actx.stroke();
        }
      } else {
        const p = toScreen(v.to.lon, v.to.lat);
        if (p[2] > 0) {
          actx.strokeStyle = `rgba(${BRAND_CSS},${0.8 * (1 - fade)})`; actx.lineWidth = 1.2 * lw;
          actx.beginPath(); actx.arc(p[0], p[1], (4 + fade * 22) * lw, 0, 7); actx.stroke();
          actx.fillStyle = `rgba(${BRAND_CSS},${1 - fade})`;
          actx.beginPath(); actx.arc(p[0], p[1], 3 * lw, 0, 7); actx.fill();
        }
      }
    }
  };

  // ── render loop ──
  let raf = 0, last = 0, destroyed = false, visibleOnScreen = true, ready = false;
  const running = () => !destroyed && visibleOnScreen && !document.hidden && !gl.isContextLost();
  const settling = () => !!drag || Math.abs(vel) > 1e-3 || Math.abs(targetHover - hover) > 0.01
    || Math.abs(targetSel - sel) > 0.01 || Math.abs(targetTilt - tilt) > 0.01;
  const frame = (t: number) => {
    raf = 0;
    const dt = Math.min(50, last ? t - last : 16);
    last = t;
    hover += (targetHover - hover) * 0.08;
    if (!drag) { rot += vel; vel *= 0.94; }                 // inertia after release
    if (!reduced) rot += dt * 0.0032;                       // ~3.2°/s
    dx += (targetDx - dx) * 0.045;
    tilt += (targetTilt - tilt) * 0.045;
    giro = rot + dx;

    hoverCountry();
    sel += (targetSel - sel) * 0.12;
    gl.useProgram(prog);
    gl.uniform2f(u.res, bW, bH); gl.uniform2f(u.c, bW / 2, geo.R); gl.uniform1f(u.r, geo.R);
    gl.uniform1f(u.rot, giro); gl.uniform1f(u.tilt, tilt / DEG); gl.uniform1f(u.dpr, dpr);
    gl.uniform1f(u.pais, pais); gl.uniform1f(u.sel, sel);
    tour(t / 1000);
    drawArcs(t / 1000);
    gl.uniform2f(u.mouse, mx - bL, my - bT); gl.uniform1f(u.hover, hover); gl.uniform1f(u.spot, 190 * k);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    if (landReady && !ready) { ready = true; o.onReady(); }
    if (running() && (!reduced || settling())) raf = requestAnimationFrame(frame);
  };
  function kick() {
    if (!raf && running()) { last = 0; raf = requestAnimationFrame(frame); }
  }
  const stop = () => { if (raf) cancelAnimationFrame(raf); raf = 0; };

  // ── input ──
  const local = (e: PointerEvent): [number, number] => {
    const r = root.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const onMove = (e: PointerEvent) => {
    const [x, y] = local(e);
    if (e.pointerType === 'mouse') {
      if (!reduced) {
        targetDx = (e.clientX / window.innerWidth - 0.5) * 16;
        // + : the surface follows the pointer vertically, like targetDx does horizontally
        targetTilt = BASE_TILT + tiltDrag + (e.clientY / window.innerHeight - 0.5) * 6;
      }
      mx = x; my = y;
      targetHover = onGlobe(x, y) ? 1 : 0;
      cv.style.cursor = drag ? 'grabbing' : targetHover ? 'grab' : '';
    }
    if (drag && e.pointerId === drag.id) {
      if (!drag.axis) {
        const ax = Math.abs(e.clientX - drag.x0), ay = Math.abs(e.clientY - drag.y0);
        if (Math.max(ax, ay) > 6) drag.axis = ax >= ay ? 'x' : 'y';
        if (drag.axis === 'y') { drag = null; return; }     // vertical swipe: leave it to page scroll
        if (!drag.axis) return;
      }
      if (drag.axis === 'xy') {
        const dy = e.clientY - drag.y;
        // a larger tilt moves the facing surface down (see toScreen), so dragging down adds tilt
        tiltDrag = clamp(tiltDrag + (dy / geo.R) * DEG, -40, 34);
        targetTilt = BASE_TILT + tiltDrag;
        tilt = targetTilt;
      }
      drag.y = e.clientY;
      const d = e.clientX - drag.x;
      drag.x = e.clientX;
      const deg = (d / geo.R) * DEG;
      rot += deg; vel = deg;
    }
    kick();
  };
  const onDown = (e: PointerEvent) => {
    const [x, y] = local(e);
    if (!onGlobe(x, y)) return;
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, axis: e.pointerType === 'touch' ? '' : 'xy' };
    vel = 0;
    if (e.pointerType !== 'touch') { cv.setPointerCapture(e.pointerId); cv.style.cursor = 'grabbing'; }
    kick();
  };
  const onUp = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    drag = null;
    if (e.pointerType === 'mouse') cv.style.cursor = 'grab';
    kick();
  };
  const onLeave = () => { targetHover = 0; mx = my = -9999; kick(); };
  const onVisibility = () => { if (document.hidden) stop(); else kick(); };
  const onLost = (e: Event) => { e.preventDefault(); stop(); o.onLost(); };

  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
  document.documentElement.addEventListener('pointerleave', onLeave);
  document.addEventListener('visibilitychange', onVisibility);
  cv.addEventListener('pointerdown', onDown);
  cv.addEventListener('webglcontextlost', onLost);
  const ro = new ResizeObserver(measure);
  ro.observe(root);
  ro.observe(box);
  const io = new IntersectionObserver(([e]) => {
    visibleOnScreen = e.isIntersecting;
    if (visibleOnScreen) kick(); else stop();
  });
  io.observe(root);
  measure();

  return () => {
    destroyed = true;
    stop();
    abort.abort();
    pulses.forEach((a) => a.cancel());
    ro.disconnect();
    io.disconnect();
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    document.documentElement.removeEventListener('pointerleave', onLeave);
    document.removeEventListener('visibilitychange', onVisibility);
    cv.removeEventListener('pointerdown', onDown);
    cv.removeEventListener('webglcontextlost', onLost);
    gl.deleteTexture(tex);
    gl.deleteBuffer(buf);
    gl.deleteProgram(prog);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    cv.remove();
    actx.setTransform(1, 0, 0, 1, 0, 0);
    actx.clearRect(0, 0, arcs.width, arcs.height);
    for (const v of slots) { v.el.dataset.on = ''; v.el.style.opacity = '0'; }
    if (label) label.dataset.on = '';
  };
}
